import { databaseTitleFromCsvPath, type DatabaseCsvIndex } from './scan-export.js';
import type { EnrichedExportNode, NotionExportNode } from './types.js';

export type ExportDatabaseEntry = {
  notionId: string;
  displayTitle: string;
  href: string;
  /** Path to the database's CSV export. */
  csvPath: string;
  /** Folder the CSV lives in, for disambiguating same-named databases. */
  location: string;
  /**
   * True when Notion exported a `_all.csv` (the full source database). Linked
   * database *views* only export a filtered `Name {id}.csv` without `_all`.
   */
  isFullExport: boolean;
  rowPages: EnrichedExportNode[];
};

export type ExportCatalog = {
  /** Full enriched tree (for parent lookup during import). */
  enrichedRoots: EnrichedExportNode[];
  pageTree: EnrichedExportNode[];
  databases: ExportDatabaseEntry[];
};

/**
 * Enrich a parsed node. `kind` is taken straight from the index.html structure
 * (a Notion collection vs a regular page), so no markdown sniffing is required.
 */
export function enrichExportNode(
  node: NotionExportNode,
  databaseCsvByNotionId: DatabaseCsvIndex,
  children: EnrichedExportNode[],
): EnrichedExportNode {
  const isDatabase = node.kind === 'database';
  const csvPath =
    isDatabase && node.notionId ? databaseCsvByNotionId.get(node.notionId) ?? null : null;
  return {
    notionId: node.notionId,
    href: node.href,
    title: node.title,
    displayTitle: node.title,
    kind: isDatabase ? 'database_with_csv' : 'page',
    csvPath,
    children,
  };
}

/** A page directly under a database collection is a database row page. */
function markDatabaseRowKinds(
  nodes: EnrichedExportNode[],
  parentIsDatabase: boolean,
): EnrichedExportNode[] {
  return nodes.map((node) => {
    const children = markDatabaseRowKinds(node.children, node.kind === 'database_with_csv');
    const kind =
      parentIsDatabase && node.kind === 'page' ? 'database_row_pages' : node.kind;
    return { ...node, kind, children };
  });
}

function folderOf(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx >= 0 ? path.slice(0, idx) : '';
}

function collectDatabaseNodes(
  nodes: EnrichedExportNode[],
  out: EnrichedExportNode[] = [],
): EnrichedExportNode[] {
  for (const node of nodes) {
    if (node.kind === 'database_with_csv') {
      out.push(node);
    }
    collectDatabaseNodes(node.children, out);
  }
  return out;
}

export function buildExportCatalog(
  enrichedRoots: EnrichedExportNode[],
  databaseCsvByNotionId: DatabaseCsvIndex,
): ExportCatalog {
  const markedRoots = markDatabaseRowKinds(enrichedRoots, false);
  const databases: ExportDatabaseEntry[] = [];
  const seen = new Set<string>();

  // Databases declared by index.html (authoritative names + row pages). Only
  // those with an actual CSV file are importable; index entries without a CSV
  // are Notion "linked database" views (references with no exported data).
  for (const node of collectDatabaseNodes(markedRoots)) {
    if (!node.notionId || seen.has(node.notionId)) {
      continue;
    }
    const csvPath = node.csvPath ?? databaseCsvByNotionId.get(node.notionId) ?? null;
    if (!csvPath) {
      continue;
    }
    seen.add(node.notionId);
    databases.push({
      notionId: node.notionId,
      displayTitle: node.displayTitle,
      href: node.href,
      csvPath,
      location: folderOf(csvPath),
      isFullExport: /_all\.csv$/i.test(csvPath),
      rowPages: node.children.filter((c) => c.notionId && c.href),
    });
  }

  // Fallback: CSV files on disk with no matching index entry.
  for (const [notionId, csvPath] of databaseCsvByNotionId) {
    if (seen.has(notionId)) {
      continue;
    }
    seen.add(notionId);
    databases.push({
      notionId,
      displayTitle: databaseTitleFromCsvPath(csvPath),
      href: '',
      csvPath,
      location: folderOf(csvPath),
      isFullExport: /_all\.csv$/i.test(csvPath),
      rowPages: [],
    });
  }

  // Full source databases first, then linked views; then by name + location.
  databases.sort(
    (a, b) =>
      Number(b.isFullExport) - Number(a.isFullExport) ||
      a.displayTitle.localeCompare(b.displayTitle) ||
      a.location.localeCompare(b.location),
  );

  function filterPageTree(nodes: EnrichedExportNode[]): EnrichedExportNode[] {
    const out: EnrichedExportNode[] = [];
    for (const node of nodes) {
      if (node.kind === 'database_with_csv') {
        continue;
      }
      out.push({
        ...node,
        children: filterPageTree(node.children),
      });
    }
    return out;
  }

  return {
    enrichedRoots: markedRoots,
    pageTree: filterPageTree(markedRoots),
    databases,
  };
}

export function countPageTreeNodes(node: EnrichedExportNode): number {
  let n = node.href ? 1 : 0;
  for (const child of node.children) {
    n += countPageTreeNodes(child);
  }
  return n;
}

/** Map each page notion id to the set of database notion ids nested under it (any depth). */
export function databasesByAncestorPage(
  catalog: ExportCatalog,
): Map<string, Set<string>> {
  const dbIds = new Set(catalog.databases.map((d) => d.notionId));
  const result = new Map<string, Set<string>>();

  function walk(
    nodes: EnrichedExportNode[],
    ancestorPageIds: string[],
  ): void {
    for (const node of nodes) {
      const nextAncestors =
        node.notionId && node.kind === 'page' && node.href
          ? [...ancestorPageIds, node.notionId]
          : ancestorPageIds;

      if (node.notionId && node.kind === 'database_with_csv' && dbIds.has(node.notionId)) {
        for (const pageId of ancestorPageIds) {
          const set = result.get(pageId) ?? new Set<string>();
          set.add(node.notionId);
          result.set(pageId, set);
        }
      }

      walk(node.children, nextAncestors);
    }
  }

  walk(catalog.enrichedRoots, []);
  return result;
}

function collectPageSubtreeIds(node: EnrichedExportNode, ids: Set<string>): void {
  if (node.notionId && node.href && node.kind === 'page') {
    ids.add(node.notionId);
  }
  for (const child of node.children) {
    collectPageSubtreeIds(child, ids);
  }
}

function findPageSubtree(node: EnrichedExportNode, rootId: string): Set<string> | null {
  if (node.notionId === rootId) {
    const ids = new Set<string>();
    collectPageSubtreeIds(node, ids);
    return ids;
  }
  for (const child of node.children) {
    const found = findPageSubtree(child, rootId);
    if (found) {
      return found;
    }
  }
  return null;
}

/** All page notion ids in the subtree rooted at `rootId` (inclusive). */
export function pageSubtreeIds(
  nodes: EnrichedExportNode[],
  rootId: string,
): Set<string> | null {
  for (const root of nodes) {
    const found = findPageSubtree(root, rootId);
    if (found) {
      return found;
    }
  }
  return null;
}
